/**
 * /frontend/src/features/analytics/api/analyticsApiClient.js
 */

import { logger } from '../../../shared/utils/logger.js';

const LOG_MODULE = 'analyticsApiClient';

const RETRY_CONFIG = Object.freeze({ maxRetries: 1, baseDelayMs: 2000 });

/**
 * Abort-aware delay. Rejects with AbortError if signal fires during the wait.
 */
const delay = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });

/**
 * Returns true if the error is retryable (network errors, 5xx).
 * Returns false for AbortError and 4xx responses.
 */
const isRetryable = (error) => {
  if (error?.name === 'AbortError') return false;
  const status = error?.status ?? error?.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return true;
};

/**
 * Unwraps the backend ApiResponse envelope.
 * @param {any} payload
 * @returns {any}
 */
export const unwrapApiData = (payload) => payload?.data ?? payload ?? {};

/**
 * Builds a query string for GET endpoints.
 * @param {Record<string, unknown>} params
 * @returns {string}
 */
export const buildQuery = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    searchParams.set(key, String(value));
  });
  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * Performs a GET request using the app's request client.
 * @param {Function} request
 * @param {string} path
 * @param {Record<string, unknown>} params
 * @param {string} token
 * @param {string} requestName
 * @param {{timeoutMs?:number, signal?:AbortSignal}} [options]
 * @returns {Promise<any>}
 */
export const getJson = async (request, path, params, token, requestName, options = {}) => {
  if (typeof request !== 'function') {
    throw new TypeError('request client is required');
  }
  const fullPath = `${path}${buildQuery(params)}`;
  const timeoutMs = Number(options?.timeoutMs ?? 30000);
  const upstreamSignal = options?.signal;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId = null;
  const setTimer = typeof window !== 'undefined' && window.setTimeout ? window.setTimeout : setTimeout;
  const clearTimer = typeof window !== 'undefined' && window.clearTimeout ? window.clearTimeout : clearTimeout;

  if (controller && upstreamSignal) {
    if (upstreamSignal.aborted) {
      controller.abort();
    } else {
      upstreamSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  if (controller && Number.isFinite(timeoutMs) && timeoutMs > 0) {
    timeoutId = setTimer(() => controller.abort(), timeoutMs);
  }

  const maxAttempts = 1 + RETRY_CONFIG.maxRetries;
  let lastError;

  try {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const payload = await request(fullPath, {
          method: 'GET',
          requestName,
          ...(controller ? { signal: controller.signal } : {})
        });
        return unwrapApiData(payload);
      } catch (error) {
        lastError = error;

        const retriesRemaining = maxAttempts - attempt - 1;
        if (retriesRemaining <= 0 || !isRetryable(error)) {
          logger.error(LOG_MODULE, 'GET failed', {
            source: { file: 'features/analytics/api/analyticsApiClient.js', method: 'getJson' },
            path,
            requestName,
            params,
            attempt: attempt + 1,
          });
          throw error;
        }

        logger.warn(LOG_MODULE, `GET failed, retrying in ${RETRY_CONFIG.baseDelayMs}ms`, {
          source: { file: 'features/analytics/api/analyticsApiClient.js', method: 'getJson' },
          path,
          requestName,
          attempt: attempt + 1,
          error: error?.message ?? String(error),
        });

        await delay(RETRY_CONFIG.baseDelayMs, controller?.signal ?? upstreamSignal);
      }
    }

    throw lastError;
  } finally {
    if (timeoutId !== null) {
      clearTimer(timeoutId);
    }
  }
};
